import { createTheme, alpha } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    surface: Palette['primary'];
  }
  interface PaletteOptions {
    surface?: PaletteOptions['primary'];
  }
}

const primary = '#1e40af';
const primaryDark = '#1e3a8a';
const accent = '#f97316';

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: primary, dark: primaryDark, light: '#3b82f6', contrastText: '#ffffff' },
    secondary: { main: accent, contrastText: '#0a0a0a' },
    error: { main: '#dc2626' },
    warning: { main: '#d97706' },
    success: { main: '#15803d' },
    info: { main: '#0284c7' },
    background: { default: '#f4f6fb', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#475569' },
    divider: '#e2e8f0',
    surface: { main: '#f8fafc', light: '#ffffff', dark: '#e2e8f0', contrastText: '#0f172a' },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.01em' },
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
      fontSize: 'clamp(1.4rem, 1.1rem + 1vw, 1.875rem)',
    },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { fontWeight: 600, letterSpacing: 0 },
    body1: { fontSize: '0.95rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
  },
  shape: { borderRadius: 10 },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(15, 23, 42, 0.04)',
    '0 1px 3px 0 rgba(15, 23, 42, 0.06), 0 1px 2px -1px rgba(15, 23, 42, 0.04)',
    '0 4px 6px -1px rgba(15, 23, 42, 0.07), 0 2px 4px -2px rgba(15, 23, 42, 0.05)',
    '0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -4px rgba(15, 23, 42, 0.05)',
    '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.06)',
    ...Array(19).fill('0 25px 50px -12px rgba(15, 23, 42, 0.18)'),
  ] as never,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f4f6fb',
          backgroundImage:
            'radial-gradient(at 0% 0%, rgba(30, 64, 175, 0.04) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(249, 115, 22, 0.04) 0px, transparent 50%)',
          backgroundAttachment: 'fixed',
        },
        ':focus-visible': {
          outline: `2px solid ${primary}`,
          outlineOffset: 2,
        },
        '::-webkit-scrollbar': { width: 10, height: 10 },
        '::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: 8 },
        '::-webkit-scrollbar-thumb:hover': { background: '#94a3b8' },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backdropFilter: 'saturate(180%) blur(8px)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          borderRight: '1px solid #e2e8f0',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ ownerState }) => ({
          backgroundImage: 'none',
          ...(ownerState.variant !== 'outlined' && {
            border: '1px solid #e2e8f0',
          }),
        }),
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: '1px solid #e2e8f0',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          paddingTop: 8,
          paddingBottom: 8,
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${primaryDark} 0%, ${primaryDark} 100%)`,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', fullWidth: true, size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, borderRadius: 6 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#f8fafc',
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: '#475569',
            fontSize: 12,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            borderBottom: '1px solid #e2e8f0',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #f1f5f9',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(primary, 0.03),
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginInline: 8,
          marginBlock: 2,
          color: '#475569',
          '& .MuiListItemIcon-root': {
            color: 'inherit',
            minWidth: 36,
          },
          '&.active, &.Mui-selected': {
            backgroundColor: alpha(primary, 0.1),
            color: primary,
            fontWeight: 600,
            '& .MuiListItemIcon-root': { color: primary },
            '&:hover': { backgroundColor: alpha(primary, 0.14) },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0f172a',
          fontSize: 12,
          padding: '6px 10px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: 3 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, minHeight: 44 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },
  },
});
